import type { SettingsRow, TariffType } from "@/lib/api"

/** Consumo mensal por período (kWh). Para bi/simples alguns ficam a 0. */
export interface PeriodKwh {
  empty: number
  peak: number
  full: number
}

/** Um mês de consumo real (Tem fatura = Sim). */
export interface ConsumptionMonth {
  month?: number | null
  empty: number
  peak: number
  full: number
}

/** Preço €/kWh por período, coerente com o motor (bi = Vazio + Fora Vazio). */
export function periodPrices(
  settings: Pick<
    SettingsRow,
    | "tariff_simple_price_eur_kwh"
    | "tariff_bi_empty_eur_kwh"
    | "tariff_bi_peak_eur_kwh"
    | "tariff_tri_empty_eur_kwh"
    | "tariff_tri_peak_eur_kwh"
    | "tariff_tri_full_eur_kwh"
  >,
  tariff: TariffType,
): PeriodKwh {
  if (tariff === "simples") {
    const p = Number(settings.tariff_simple_price_eur_kwh) || 0
    return { empty: p, peak: p, full: p }
  }
  if (tariff === "tri_horaria") {
    return {
      empty: Number(settings.tariff_tri_empty_eur_kwh) || 0,
      peak: Number(settings.tariff_tri_peak_eur_kwh) || 0,
      full: Number(settings.tariff_tri_full_eur_kwh) || 0,
    }
  }
  // Bi-horária: só Vazio + Fora Vazio (peak). Cheias partilha o Fora Vazio.
  const foraVazio = Number(settings.tariff_bi_peak_eur_kwh) || 0
  return {
    empty: Number(settings.tariff_bi_empty_eur_kwh) || 0,
    peak: foraVazio,
    full: foraVazio,
  }
}

/**
 * Estimativa dos kWh mensais por período a partir do valor da fatura:
 *   energia = valor × (1 − custos_fixos); por período = energia × dist ÷ preço.
 * `dist` são fracções (somam 1). Para simples, tudo em `full`.
 */
export function estimateMonthlyKwh(
  billEur: number,
  fixedCostFraction: number,
  dist: PeriodKwh,
  prices: PeriodKwh,
): PeriodKwh {
  const energy = Math.max(0, billEur) * (1 - Math.min(1, Math.max(0, fixedCostFraction)))
  const per = (fraction: number, price: number) =>
    price > 0 ? (energy * fraction) / price : 0
  return {
    empty: per(dist.empty, prices.empty),
    peak: per(dist.peak, prices.peak),
    full: per(dist.full, prices.full),
  }
}

/** Média mensal por período a partir dos meses reais preenchidos. */
export function averageMonths(months: ConsumptionMonth[]): PeriodKwh {
  const valid = months.filter(
    (m) => Number(m.empty) + Number(m.peak) + Number(m.full) > 0,
  )
  if (valid.length === 0) return { empty: 0, peak: 0, full: 0 }
  const sum = valid.reduce(
    (acc, m) => ({
      empty: acc.empty + (Number(m.empty) || 0),
      peak: acc.peak + (Number(m.peak) || 0),
      full: acc.full + (Number(m.full) || 0),
    }),
    { empty: 0, peak: 0, full: 0 },
  )
  return {
    empty: sum.empty / valid.length,
    peak: sum.peak / valid.length,
    full: sum.full / valid.length,
  }
}

export function periodTotal(p: PeriodKwh): number {
  return p.empty + p.peak + p.full
}

/** Distribuição (fracções que somam 1) a partir de kWh por período. */
export function toDistribution(p: PeriodKwh): PeriodKwh {
  const t = periodTotal(p)
  if (t <= 0) return { empty: 0, peak: 0, full: 1 }
  return { empty: p.empty / t, peak: p.peak / t, full: p.full / t }
}
